import { DatePicker } from '../../../components/ui/date-picker';
// apps/web/src/pages/vendas/precos/nova-versao-sheet.tsx — Fase 12, Plano 06 (PV-09, D-13, D-14).
// FormDialog "Nova versão de preço" do serviço (cliente + item) ou da tinta (material + fornecedor). A versão
// anterior encerra na véspera da nova data (regra do servidor); 409 VIGENCIA_SOBREPOSTA mostra a mensagem
// da API sem fechar o FormDialog.
import { useEffect, useState, type FormEvent } from 'react';
import type {
  ClienteLookupPv,
  MaterialLookupPv,
  NovaVersaoPrecoServicoInput,
  NovaVersaoPrecoTintaInput,
  PrecoServicoVersaoView,
  PrecoTintaVersaoView,
} from '@pieltec/contracts';
import { ClienteCombobox } from '../../../components/vendas/cliente-combobox';
import { Button } from '../../../components/ui/button';
import { FormMessage } from '../../../components/ui/form-message';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { apiMutate } from '../../../lib/csrf-fetch';
import { lerErroApi } from '../../../lib/vendas/erro-api';
import { hojeIso } from '../../../lib/vendas/formatacao';
import { lerNumeroDigitado, mensagemNumeroDigitado } from '../../../lib/vendas/numero';
import { CASAS_PRECO_PV, PRECO_MAXIMO_ITEM_PV, temNoMaximoCasas } from '../../../lib/vendas/validacao-pv';
import { MaterialCombobox } from '../../../components/vendas/material-combobox';

interface NovaVersaoSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** Preço de serviço (cliente + item) ou de tinta (material + fornecedor). */
  readonly alvo: 'SERVICO' | 'TINTA';
  /** Linha de serviço de origem ("Nova versão" por linha: cliente e item travados); null = "Novo preço". */
  readonly base: PrecoServicoVersaoView | null;
  /** Linha de tinta de origem (tinta travada); null = "Novo preço". */
  readonly baseTinta?: PrecoTintaVersaoView | null;
  readonly onSalvo: () => void;
}

interface Erros {
  cliente?: string;
  item?: string;
  fornecedor?: string;
  unidade?: string;
  preco?: string;
  data?: string;
  motivo?: string;
}

const UNIDADE_PADRAO = 'peça';

export async function lerMensagemErro(res: Response, padrao: string): Promise<{ codigo: string | null; mensagem: string }> {
  const erro = await lerErroApi(res, padrao);
  return { codigo: erro.codigo, mensagem: erro.mensagem };
}

export function NovaVersaoSheet({ aberto, onAbertoChange, alvo, base, baseTinta = null, onSalvo }: NovaVersaoSheetProps) {
  const tinta = alvo === 'TINTA';
  const travado = tinta ? baseTinta !== null : base !== null;
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [material, setMaterial] = useState<MaterialLookupPv | null>(null);
  const [fornecedor, setFornecedor] = useState('');
  const [unidade, setUnidade] = useState(UNIDADE_PADRAO);
  const [preco, setPreco] = useState('');
  const [inicio, setInicio] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [erroApi, setErroApi] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setCliente(base ? { id: base.clienteId, codigo: '', nome: base.clienteNome } : null);
    if (tinta) {
      setMaterial(baseTinta ? { id: baseTinta.tintaMaterialId, codigo: baseTinta.tintaCodigo, nome: baseTinta.tintaNome } : null);
      setFornecedor(baseTinta?.fornecedorNome ?? '');
      setUnidade(baseTinta?.unidadeConsumo ?? UNIDADE_PADRAO);
    } else {
      setMaterial(base ? { id: base.materialId, codigo: base.materialCodigo, nome: base.materialNome } : null);
      setFornecedor('');
      setUnidade(base?.unidade ?? UNIDADE_PADRAO);
    }
    setPreco('');
    setInicio(hojeIso());
    setMotivo('');
    setErros({});
    setErroApi(null);
  }, [aberto, base, baseTinta, tinta]);

  const dataPassada = inicio !== '' && inicio < hojeIso();

  function validar(): NovaVersaoPrecoServicoInput | NovaVersaoPrecoTintaInput | null {
    const proximos: Erros = {};
    const valor = lerNumeroDigitado(preco);
    if (!tinta && !cliente) proximos.cliente = 'Selecione o cliente.';
    if (!material) proximos.item = tinta ? 'Selecione a tinta.' : 'Selecione o item.';
    if (fornecedor.length > 120) proximos.fornecedor = 'O fornecedor aceita até 120 caracteres.';
    if (!unidade.trim()) proximos.unidade = 'Informe a unidade.';
    if (!Number.isFinite(valor) || valor < 0) proximos.preco = mensagemNumeroDigitado(preco, 'Informe um preço válido (zero ou maior).');
    else if (valor > PRECO_MAXIMO_ITEM_PV) proximos.preco = 'O preço informado é alto demais.';
    else if (!temNoMaximoCasas(valor, CASAS_PRECO_PV)) proximos.preco = `Use no máximo ${CASAS_PRECO_PV} casas decimais no preço.`;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(inicio)) proximos.data = 'Informe a data de início da vigência.';
    if (motivo.trim().length < 5) proximos.motivo = 'Informe o motivo (mínimo de 5 caracteres).';
    setErros(proximos);
    if (Object.keys(proximos).length > 0 || !material || (!tinta && !cliente)) return null;
    if (tinta) {
      return {
        tintaMaterialId: material.id,
        fornecedorNome: fornecedor.trim() === '' ? null : fornecedor.trim(),
        unidadeConsumo: unidade.trim(),
        preco: valor,
        vigenciaInicio: inicio,
        motivo: motivo.trim(),
      };
    }
    if (!cliente) return null;
    return {
      clienteId: cliente.id,
      materialId: material.id,
      unidade: unidade.trim(),
      preco: valor,
      vigenciaInicio: inicio,
      motivo: motivo.trim(),
    };
  }

  async function salvar(event: FormEvent): Promise<void> {
    event.preventDefault();
    const corpo = validar();
    if (!corpo) return;
    setSalvando(true);
    setErroApi(null);
    try {
      const res = await apiMutate(`/api/v1/vendas/precos/${tinta ? 'tintas' : 'servicos'}`, { body: corpo });
      if (!res.ok) {
        const erro = await lerMensagemErro(res, 'Não foi possível salvar a versão de preço.');
        setErroApi(erro.mensagem);
        return;
      }
      onSalvo();
      onAbertoChange(false);
    } catch {
      setErroApi('Não foi possível salvar a versão de preço. Verifique a conexão e tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <form onSubmit={(e) => void salvar(e)} className="flex h-full flex-col gap-4" noValidate>
          <FormDialogHeader>
            <FormDialogTitle>Nova versão de preço</FormDialogTitle>
            <FormDialogDescription>A versão anterior é mantida no histórico e encerra na véspera desta data.</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-col gap-4 px-4">
            {!tinta && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="preco-cliente">Cliente</Label>
                <ClienteCombobox id="preco-cliente" value={cliente} onChange={setCliente} disabled={travado} invalido={!!erros.cliente} />
                {erros.cliente && <FormMessage>{erros.cliente}</FormMessage>}
              </div>
            )}

            <div className="flex flex-col gap-1">
              <Label htmlFor="preco-item">{tinta ? 'Tinta' : 'Item'}</Label>
              <MaterialCombobox
                id="preco-item"
                value={material}
                disabled={travado}
                invalido={!!erros.item}
                onChange={setMaterial}
              />
              {erros.item && <FormMessage>{erros.item}</FormMessage>}
            </div>

            {tinta && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="preco-fornecedor">Fornecedor</Label>
                <Input id="preco-fornecedor" value={fornecedor} maxLength={120} onChange={(e) => setFornecedor(e.target.value)} aria-invalid={!!erros.fornecedor} />
                {erros.fornecedor && <FormMessage>{erros.fornecedor}</FormMessage>}
              </div>
            )}

            <div className="flex flex-col gap-1">
              <Label htmlFor="preco-unidade">{tinta ? 'Unidade de consumo' : 'Unidade'}</Label>
              <Input id="preco-unidade" value={unidade} disabled={!tinta && travado} onChange={(e) => setUnidade(e.target.value)} aria-invalid={!!erros.unidade} />
              {erros.unidade && <FormMessage>{erros.unidade}</FormMessage>}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="preco-valor">Preço</Label>
              <Input id="preco-valor" inputMode="decimal" placeholder="0,00" value={preco} onChange={(e) => setPreco(e.target.value)} aria-invalid={!!erros.preco} />
              {erros.preco && <FormMessage>{erros.preco}</FormMessage>}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="preco-inicio">Vigente a partir de</Label>
              <DatePicker id="preco-inicio" value={inicio} onValueChange={(value) => setInicio(value)} aria-invalid={!!erros.data} />
              {dataPassada && (
                <p className="text-xs text-amber-700" role="status">
                  Data anterior a hoje. PVs já precificados não mudam.
                </p>
              )}
              {erros.data && <FormMessage>{erros.data}</FormMessage>}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="preco-motivo">Motivo</Label>
              <Input id="preco-motivo" value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} aria-invalid={!!erros.motivo} />
              {erros.motivo && <FormMessage>{erros.motivo}</FormMessage>}
            </div>

            {erroApi && <FormMessage>{erroApi}</FormMessage>}
          </div>

          <FormDialogFooter>
            <Button type="submit" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar versão'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)}>
              Cancelar
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
